// ---------------------------------------------------------------------------
// DnD Email Editor – Email-Safe HTML Compiler
//
// Produces table-based, inline-styled HTML that renders correctly across
// Gmail, Outlook desktop/web/mobile, Apple Mail, Yahoo, and other clients.
// ---------------------------------------------------------------------------

import type { DndEmailModel, GlobalStyles, Spacing, BorderSides, Layer, TextLayer, ImageLayer, ButtonLayer, DividerLayer, SpacerLayer, HtmlLayer, ColumnsLayer, SocialLayer, VideoLayer, CountdownLayer, Element } from './dnd-model';
import { hasSpacing, hasVisibleBorder } from './dnd-model';
import { DND_HTML_MARKER } from './dnd-marker';
import { socialIconHtml } from './dnd-social-icons';
import { getColumnWidths, getColumnPixelWidths } from './dnd-columns';
import { typographyCss, inlineContentStyles, resolveTextColor } from './dnd-typography';
import { fontFamilyCss, webFontHeadHtml, webFontsUsedIn } from './dnd-webfonts';

// via.placeholder.com was discontinued; placehold.co is a live drop-in. These
// only appear when a block is left without a real image (should not ship).
const IMAGE_PLACEHOLDER = 'https://placehold.co/600x200/e0e0e0/999999?text=Add+an+image';

// ---- Style helpers --------------------------------------------------------

function spacingCss(s: Spacing): string {
  return `${s.top}px ${s.right}px ${s.bottom}px ${s.left}px`;
}

const ZERO_SPACING: Spacing = { top: 0, right: 0, bottom: 0, left: 0 };

function borderCss(b: BorderSides): string {
  const sides: string[] = [];
  if (b.top.style !== 'none' && b.top.width > 0)
    sides.push(`border-top: ${b.top.width}px ${b.top.style} ${b.top.color};`);
  if (b.right.style !== 'none' && b.right.width > 0)
    sides.push(`border-right: ${b.right.width}px ${b.right.style} ${b.right.color};`);
  if (b.bottom.style !== 'none' && b.bottom.width > 0)
    sides.push(`border-bottom: ${b.bottom.width}px ${b.bottom.style} ${b.bottom.color};`);
  if (b.left.style !== 'none' && b.left.width > 0)
    sides.push(`border-left: ${b.left.width}px ${b.left.style} ${b.left.color};`);
  if (b.radius && b.radius > 0) sides.push(`border-radius: ${b.radius}px;`);
  return sides.join(' ');
}

function bgCss(layer: Layer): string {
  const parts: string[] = [];
  if (layer.bgColor) parts.push(`background-color: ${layer.bgColor};`);
  if (layer.bgImage) {
    parts.push(`background-image: url('${escHtml(layer.bgImage)}');`);
    parts.push(`background-repeat: ${layer.bgRepeat || 'no-repeat'};`);
    parts.push(`background-position: ${layer.bgPosition || 'center center'};`);
    parts.push(`background-size: ${layer.bgSize || 'cover'};`);
  }
  return parts.join(' ');
}

function vmlBgImage(layer: Layer, innerHtml: string, maxWidth: number): string {
  if (!layer.bgImage) return innerHtml;
  const fill = layer.bgColor ? ` fillcolor="${escHtml(layer.bgColor)}"` : '';
  return `<!--[if gte mso 9]>
<v:rect xmlns:v="urn:schemas-microsoft-com:vml" fill="true" stroke="false" style="width:${maxWidth}px;"${fill}>
<v:fill type="tile" src="${escHtml(layer.bgImage)}" />
<v:textbox style="mso-fit-shape-to-text:true" inset="0,0,0,0">
<![endif]-->
${innerHtml}
<!--[if gte mso 9]>
</v:textbox>
</v:rect>
<![endif]-->`;
}

function escHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Make a user-entered link email-safe by giving it a scheme so it works in a
 * mail client. A bare email becomes a mailto:, a bare domain becomes https://.
 * Already-qualified URLs, anchors, relative paths, and {{merge_tags}} are left
 * untouched. (Without this a social "email" link like info@x.com compiles to
 * href="info@x.com", which does nothing when clicked.)
 */
function normalizeUrl(url: string): string {
  const u = (url || '').trim();
  if (!u) return u;
  if (u.includes('{{') || u.includes('}}')) return u;               // merge tags
  if (/^(https?:|mailto:|tel:|sms:|ftp:)/i.test(u)) return u;        // already scheme'd
  if (u.startsWith('#') || u.startsWith('/')) return u;              // anchor / relative
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(u)) return `mailto:${u}`;    // bare email address
  return `https://${u}`;                                            // bare domain
}

/**
 * Href for a social link. The "email" platform must always be a mailto: — its
 * URL is typically the {{facility_email}} merge tag, which resolves to a bare
 * address at send time and would otherwise be a dead link. Other platforms go
 * through normalizeUrl.
 */
function socialHref(platform: string, url: string): string {
  const u = (url || '').trim();
  if (!u) return '';
  if (platform === 'email') return /^mailto:/i.test(u) ? u : `mailto:${u}`;
  return normalizeUrl(u);
}

function dataAttrs(id: string, type: string, extra: Record<string, string> = {}): string {
  let s = ` data-ftp-node-id="${escHtml(id)}" data-ftp-type="${escHtml(type)}"`;
  for (const [k, v] of Object.entries(extra)) {
    s += ` data-ftp-${k}="${escHtml(v)}"`;
  }
  return s;
}

function unlockAttr(locked: boolean, type: string): string {
  if (locked) return '';
  if (type === 'text' || type === 'image') return ' data-ftp-unlocked="true"';
  return '';
}

function visibilityClass(layer: { hideOnMobile: boolean; hideOnDesktop: boolean }): string {
  const cls: string[] = [];
  if (layer.hideOnMobile) cls.push('dnd-hide-mobile');
  if (layer.hideOnDesktop) cls.push('dnd-hide-desktop');
  return cls.length ? ` class="${cls.join(' ')}"` : '';
}

// ---- TABLE style constants ------------------------------------------------

const TABLE_RESET =
  'border-collapse: collapse; mso-table-lspace: 0pt; mso-table-rspace: 0pt;';

// ---- Per-layer compilers --------------------------------------------------

function compileTextLayer(layer: TextLayer, gs: GlobalStyles): string {
  const style = [
    `padding: ${spacingCss(layer.padding)};`,
    !layer.bgImage && layer.bgColor ? `background-color: ${layer.bgColor};` : '',
    borderCss(layer.border),
    fontFamilyCss(gs.fontFamily),
    `font-size: ${gs.fontSize};`,
    `color: ${resolveTextColor(layer.textColor, gs.textColor)};`,
    `line-height: ${layer.lineHeight || gs.lineHeight};`,
    `text-align: ${layer.textAlign};`,
    'mso-line-height-rule: exactly;',
    'word-break: break-word;',
  ]
    .filter(Boolean)
    .join(' ');

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'text')}${unlockAttr(layer.locked, 'text')}${visibilityClass(layer)}>
  <tr>
    <td data-dnd-editable="true" style="${style}">
      ${inlineContentStyles(layer.content, gs)}
    </td>
  </tr>
</table>`;
}

function imgAlignMarginCss(align: string): string {
  if (align === 'center') return 'margin: 0 auto;';
  if (align === 'right') return 'margin: 0 0 0 auto;';
  return '';
}

function compileImageLayer(layer: ImageLayer, _gs: GlobalStyles, maxWidth: number): string {
  const cssWidth = layer.autoWidth ? maxWidth : Math.min(layer.width, maxWidth);
  const htmlWidth = layer.retina ? Math.round(cssWidth / 2) : cssWidth;
  const effectiveSrc = layer.src || IMAGE_PLACEHOLDER;
  const alignMap = { left: 'left', center: 'center', right: 'right' } as const;

  const imgStyle = [
    'display: block;',
    `max-width: ${cssWidth}px;`,
    'width: 100%;',
    'height: auto;',
    layer.borderRadius ? `border-radius: ${layer.borderRadius}px;` : '',
    imgAlignMarginCss(layer.align),
  ]
    .filter(Boolean)
    .join(' ');

  const tdStyle = [
    `padding: ${spacingCss(layer.padding)};`,
    `text-align: ${alignMap[layer.align]};`,
    !layer.bgImage && layer.bgColor ? `background-color: ${layer.bgColor};` : '',
    borderCss(layer.border),
  ]
    .filter(Boolean)
    .join(' ');

  const imgTag = `<img src="${escHtml(effectiveSrc)}" alt="${escHtml(layer.alt || 'Image')}" width="${htmlWidth}" border="0" style="${imgStyle}" data-dnd-image="true" />`;
  const linked = layer.href
    ? `<a href="${escHtml(normalizeUrl(layer.href))}" target="${layer.target}" style="text-decoration: none;">${imgTag}</a>`
    : imgTag;

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'image')}${unlockAttr(layer.locked, 'image')}${visibilityClass(layer)}>
  <tr>
    <td align="${alignMap[layer.align]}" style="${tdStyle}">
      ${linked}
    </td>
  </tr>
</table>`;
}

function compileButtonLayer(layer: ButtonLayer, gs: GlobalStyles): string {
  // A button with no link is a dead button — drop it from the sent email.
  if (!layer.href || !layer.href.trim()) return '';
  const bp = layer.buttonPadding;
  const tdStyle = [
    `padding: ${spacingCss(layer.padding)};`,
    `text-align: ${layer.align};`,
    !layer.bgImage && layer.bgColor ? `background-color: ${layer.bgColor};` : '',
    borderCss(layer.border),
  ]
    .filter(Boolean)
    .join(' ');

  const btnTdStyle = [
    `background-color: ${layer.buttonBgColor || gs.defaultButtonStyle.bgColor};`,
    `border-radius: ${layer.borderRadius}px;`,
    `padding: ${bp.top}px ${bp.right}px ${bp.bottom}px ${bp.left}px;`,
    'text-align: center;',
  ].join(' ');

  const linkStyle = [
    `color: ${layer.textColor || gs.defaultButtonStyle.textColor};`,
    fontFamilyCss(layer.fontFamily),
    `font-size: ${layer.fontSize};`,
    `font-weight: ${layer.fontWeight};`,
    'text-decoration: none;',
    'display: inline-block;',
  ].join(' ');

  const widthAttr = layer.fullWidth ? ' width="100%"' : '';
  const alignAttr = layer.fullWidth ? '' : ` align="${layer.align}"`;

  let button = `<table role="presentation" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${widthAttr}${alignAttr}>
      <tr>
        <td style="${btnTdStyle}">
          <a href="${escHtml(normalizeUrl(layer.href))}" target="${layer.target}" style="${linkStyle}">${escHtml(layer.text)}</a>
        </td>
      </tr>
    </table>`;

  if (layer.borderRadius > 0) {
    const vmlWidth = layer.fullWidth ? 600 : 200;
    // Same fallback as the HTML path (btnTdStyle) so a cleared color never
    // renders a black VML button in Outlook.
    const vmlBg = layer.buttonBgColor || gs.defaultButtonStyle.bgColor;
    button = `<!--[if mso]>
    <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${escHtml(normalizeUrl(layer.href))}" style="height:auto; v-text-anchor:middle; width:${vmlWidth}px;" arcsize="${Math.round((layer.borderRadius / 40) * 100)}%" strokecolor="${vmlBg}" fillcolor="${vmlBg}">
      <w:anchorlock/>
      <center style="${linkStyle}">${escHtml(layer.text)}</center>
    </v:roundrect>
    <![endif]-->
    <!--[if !mso]><!-->${button}<!--<![endif]-->`;
  }

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'button')}${visibilityClass(layer)}>
  <tr>
    <td style="${tdStyle}">
      ${button}
    </td>
  </tr>
</table>`;
}

function compileDividerLayer(layer: DividerLayer, _gs: GlobalStyles): string {
  const tdStyle = [
    `padding: ${spacingCss(layer.padding)};`,
    !layer.bgImage && layer.bgColor ? `background-color: ${layer.bgColor};` : '',
    borderCss(layer.border),
    `text-align: ${layer.align};`,
  ]
    .filter(Boolean)
    .join(' ');

  const hrStyle = [
    `border: 0;`,
    `border-top: ${layer.borderWidth}px ${layer.borderStyle} ${layer.borderColor || '#cccccc'};`,
    `width: ${layer.dividerWidth};`,
    'margin: 0;',
    layer.align === 'left'
      ? 'margin-right: auto;'
      : layer.align === 'right'
        ? 'margin-left: auto;'
        : 'margin-left: auto; margin-right: auto;',
  ].join(' ');

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'divider')}${visibilityClass(layer)}>
  <tr>
    <td style="${tdStyle}">
      <hr style="${hrStyle}" />
    </td>
  </tr>
</table>`;
}

function compileSpacerLayer(layer: SpacerLayer): string {
  const tdStyle = [
    `height: ${layer.height}px;`,
    `font-size: 1px;`,
    `line-height: ${layer.height}px;`,
    !layer.bgImage && layer.bgColor ? `background-color: ${layer.bgColor};` : '',
  ]
    .filter(Boolean)
    .join(' ');

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'spacer')}${visibilityClass(layer)}>
  <tr>
    <td style="${tdStyle}">&nbsp;</td>
  </tr>
</table>`;
}

function compileHtmlLayer(layer: HtmlLayer): string {
  const tdStyle = [
    `padding: ${spacingCss(layer.padding)};`,
    !layer.bgImage && layer.bgColor ? `background-color: ${layer.bgColor};` : '',
    borderCss(layer.border),
  ]
    .filter(Boolean)
    .join(' ');

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'html')}${visibilityClass(layer)}>
  <tr>
    <td style="${tdStyle}">
      ${layer.content}
    </td>
  </tr>
</table>`;
}

// ---- Element compilers (inside columns) -----------------------------------

function compileElement(el: Element, gs: GlobalStyles, colWidth: number): string {
  const bordered = hasVisibleBorder(el.border);
  const margined = hasSpacing(el.margin);
  if (!bordered && !margined) return compileElementCore(el, gs, colWidth);

  // Once there is a border, the element's padding must live on a real <td>
  // inside it — Outlook's Word engine ignores padding on a <div>, which would
  // leave the content touching the border. So the core compiles with zero
  // padding and the bordered cell carries it instead. Margin (outer spacing)
  // becomes padding on one more wrapping cell, the only outer-spacing
  // mechanism Outlook honours. Neither wrapper carries data-ftp attributes, so
  // the parser and the locked-edit sync still target the core node.
  const core = compileElementCore(
    bordered ? ({ ...el, padding: ZERO_SPACING } as Element) : el,
    gs,
    colWidth,
  );
  if (!core) return core; // e.g. a link-less button compiles to nothing

  let out = core;
  if (bordered) {
    const tdStyle = [borderCss(el.border), `padding: ${spacingCss(el.padding)};`].filter(Boolean).join(' ');
    out = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${margined ? '' : visibilityClass(el)}>
  <tr><td style="${tdStyle}">${out}</td></tr>
</table>`;
  }
  if (margined) {
    out = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${visibilityClass(el)}>
  <tr><td style="padding: ${spacingCss(el.margin)};">${out}</td></tr>
</table>`;
  }
  return out;
}

function compileElementCore(el: Element, gs: GlobalStyles, colWidth: number): string {
  switch (el.type) {
    case 'text':
      return compileTextElement(el, gs);
    case 'image':
      return compileImageElement(el, gs, colWidth);
    case 'button':
      return compileButtonElement(el, gs);
    case 'divider':
      return compileDividerElement(el);
    case 'spacer':
      return compileSpacerElement(el);
    case 'html':
      return compileHtmlElement(el);
    case 'social':
      return compileSocialElement(el);
  }
}

function compileTextElement(el: import('./dnd-model').TextElement, gs: GlobalStyles): string {
  const style = [
    `padding: ${spacingCss(el.padding)};`,
    fontFamilyCss(gs.fontFamily),
    `font-size: ${gs.fontSize};`,
    `color: ${resolveTextColor(gs.textColor)};`,
    `line-height: ${el.lineHeight || gs.lineHeight};`,
    `text-align: ${el.textAlign};`,
    'mso-line-height-rule: exactly;',
    'word-break: break-word;',
  ].join(' ');

  return `<div${dataAttrs(el.id, 'text')}${unlockAttr(el.locked ?? true, 'text')}${visibilityClass(el)} style="${style}" data-dnd-editable="true">
  ${inlineContentStyles(el.content, gs)}
</div>`;
}

function compileImageElement(el: import('./dnd-model').ImageElement, _gs: GlobalStyles, colWidth: number): string {
  // Auto-width images fill the column exactly (max-width:100%), so no sub-pixel
  // seam shows between adjacent cells. Fixed-width images cap at their px width
  // (never exceeding the column).
  const pxCap = Math.min(el.width, colWidth);
  const displayWidth = el.autoWidth ? colWidth : pxCap;
  const htmlWidth = el.retina ? Math.round(displayWidth / 2) : displayWidth;
  const maxWidthCss = el.autoWidth ? '100%' : `${pxCap}px`;
  const effectiveSrc = el.src || IMAGE_PLACEHOLDER;
  const imgStyle = [
    'display: block;',
    `max-width: ${maxWidthCss};`,
    'width: 100%;',
    'height: auto;',
    el.borderRadius ? `border-radius: ${el.borderRadius}px;` : '',
    imgAlignMarginCss(el.align),
  ]
    .filter(Boolean)
    .join(' ');

  const wrapStyle = [
    `padding: ${spacingCss(el.padding)};`,
    `text-align: ${el.align};`,
  ].join(' ');

  const img = `<img src="${escHtml(effectiveSrc)}" alt="${escHtml(el.alt || 'Image')}" width="${htmlWidth}" border="0" style="${imgStyle}" data-dnd-image="true" />`;
  const linked = el.href
    ? `<a href="${escHtml(normalizeUrl(el.href))}" target="${el.target}" style="text-decoration: none;">${img}</a>`
    : img;

  return `<div${dataAttrs(el.id, 'image')}${unlockAttr(el.locked ?? true, 'image')}${visibilityClass(el)} style="${wrapStyle}">
  ${linked}
</div>`;
}

function compileButtonElement(el: import('./dnd-model').ButtonElement, gs: GlobalStyles): string {
  // A button with no link is a dead button — drop it from the sent email.
  if (!el.href || !el.href.trim()) return '';
  const bp = el.buttonPadding;
  const btnTdStyle = [
    `background-color: ${el.bgColor || gs.defaultButtonStyle.bgColor};`,
    `border-radius: ${el.borderRadius}px;`,
    `padding: ${bp.top}px ${bp.right}px ${bp.bottom}px ${bp.left}px;`,
    'text-align: center;',
  ].join(' ');
  const linkStyle = [
    `color: ${el.textColor || gs.defaultButtonStyle.textColor};`,
    fontFamilyCss(el.fontFamily),
    `font-size: ${el.fontSize};`,
    `font-weight: ${el.fontWeight};`,
    'text-decoration: none;',
    'display: inline-block;',
  ].join(' ');
  const widthAttr = el.fullWidth ? ' width="100%"' : '';
  const alignAttr = el.fullWidth ? '' : ` align="${el.align}"`;

  return `<div${dataAttrs(el.id, 'button')}${visibilityClass(el)} style="padding: ${spacingCss(el.padding)}; text-align: ${el.align};">
  <table role="presentation" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${widthAttr}${alignAttr}>
    <tr>
      <td style="${btnTdStyle}">
        <a href="${escHtml(normalizeUrl(el.href))}" target="${el.target}" style="${linkStyle}">${escHtml(el.text)}</a>
      </td>
    </tr>
  </table>
</div>`;
}

function compileDividerElement(el: import('./dnd-model').DividerElement): string {
  const hrStyle = [
    'border: 0;',
    `border-top: ${el.borderWidth}px ${el.borderStyle} ${el.borderColor || '#cccccc'};`,
    `width: ${el.dividerWidth};`,
    'margin: 0 auto;',
  ].join(' ');
  return `<div${dataAttrs(el.id, 'divider')}${visibilityClass(el)} style="padding: ${spacingCss(el.padding)};">
  <hr style="${hrStyle}" />
</div>`;
}

function compileSpacerElement(el: import('./dnd-model').SpacerElement): string {
  return `<div${dataAttrs(el.id, 'spacer')}${visibilityClass(el)} style="height: ${el.height}px; font-size: 1px; line-height: ${el.height}px;">&nbsp;</div>`;
}

function compileHtmlElement(el: import('./dnd-model').HtmlElement): string {
  return `<div${dataAttrs(el.id, 'html')}${visibilityClass(el)} style="padding: ${spacingCss(el.padding)};">
  ${el.content}
</div>`;
}

function compileSocialElement(el: import('./dnd-model').SocialElement): string {
  // Only render enabled platforms that have a URL — an icon with no link is
  // dropped from the sent email rather than shown as a dead button.
  const enabledLinks = el.links.filter((l) => l.enabled && l.url.trim() !== '');
  const wrapStyle = [
    `padding: ${spacingCss(el.padding)};`,
    `text-align: ${el.align};`,
    el.iconColor ? `color: ${el.iconColor};` : '',
    el.lineHeight ? `line-height: ${el.lineHeight};` : '',
    el.lineHeight ? 'mso-line-height-rule: exactly;' : '',
  ]
    .filter(Boolean)
    .join(' ');
  if (!enabledLinks.length) {
    return `<div${dataAttrs(el.id, 'social')}${visibilityClass(el)} style="${wrapStyle}">&nbsp;</div>`;
  }

  const icons = enabledLinks
    .map((link) => {
      const label = link.platform.charAt(0).toUpperCase() + link.platform.slice(1);
      const showText = el.style !== 'icons-only';
      const showIcon = el.style !== 'text-only';
      const content = [
        showIcon ? socialIconHtml(link.platform, el.iconSize, el.iconColor, el.logos?.[link.platform]) : '',
        showText ? `<span style="vertical-align: middle; margin-left: 4px;">${label}</span>` : '',
      ]
        .filter(Boolean)
        .join('');
      const href = socialHref(link.platform, link.url);
      const inner = href
        ? `<a data-ftp-social="${escHtml(link.platform)}" href="${escHtml(href)}" target="_blank" title="${escHtml(label)}" style="text-decoration: none; color: inherit;">${content}</a>`
        : content;
      return `<td style="padding: 0 ${el.spacing}px;">${inner}</td>`;
    })
    .join('\n');

  return `<div${dataAttrs(el.id, 'social')}${visibilityClass(el)} style="${wrapStyle}">
  <table role="presentation" cellpadding="0" cellspacing="0" align="${el.align}" style="${TABLE_RESET}">
    <tr>${icons}</tr>
  </table>
</div>`;
}

// ---- Columns layer --------------------------------------------------------

function compileColumnsLayer(layer: ColumnsLayer, gs: GlobalStyles, maxWidth: number): string {
  const widths = getColumnWidths(layer);
  const pxWidths = getColumnPixelWidths(layer, maxWidth);
  const gap = layer.columnsGap;
  const stack = layer.stackOnMobile !== false;
  const reverse = stack && layer.reverseStackOnMobile === true;
  // Cells stack full-width on mobile only when stacking is enabled for this layer.
  const cellClass = `dnd-column${stack ? ' dnd-column-stack' : ''}`;
  const innerTableClass = reverse ? 'dnd-columns-reverse' : '';

  const tdStyle = [
    `padding: ${spacingCss(layer.padding)};`,
    !layer.bgImage && layer.bgColor ? `background-color: ${layer.bgColor};` : '',
    borderCss(layer.border),
  ]
    .filter(Boolean)
    .join(' ');

  const columnHtml = layer.columns
    .map((col, i) => {
      const pct = widths[i] ?? widths[0];
      const pxWidth = pxWidths[i] ?? pxWidths[0];
      const colStyle = [
        `width: ${pct}%;`,
        `max-width: ${pxWidth}px;`,
        col.bgColor ? `background-color: ${col.bgColor};` : '',
        `padding: ${spacingCss(col.padding)};`,
        borderCss(col.border),
        `vertical-align: ${col.valign};`,
        gap > 0 && i < layer.columns.length - 1 ? `padding-right: ${gap}px;` : '',
      ]
        .filter(Boolean)
        .join(' ');

      const elements = col.elements
        .map((el) => compileElement(el, gs, pxWidth))
        .join('\n');

      // A real width="" attribute (not just on the MSO ghost cell) so column
      // widths survive in clients that strip <style>/class; percentage + max-width
      // in the style give fluid behaviour where CSS is honoured. On mobile the
      // stacking media query overrides both with width:100% !important.
      return `<!--[if mso]><td width="${pxWidth}" valign="${col.valign}" style="${colStyle}"><![endif]-->
      <td${dataAttrs(col.id, 'column')} data-dnd-column-id="${escHtml(col.id)}" class="${cellClass}" width="${pxWidth}"${col.bgColor ? ` bgcolor="${col.bgColor}"` : ''} style="${colStyle}" valign="${col.valign}">
        ${elements || '&nbsp;'}
      </td>
      <!--[if mso]></td><![endif]-->`;
    })
    .join('\n');

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'columns', { 'column-layout': layer.layout })}${visibilityClass(layer)}>
  <tr>
    <td style="${tdStyle}">
      <!--[if mso]><table role="presentation" width="100%" cellpadding="0" cellspacing="0"><tr><![endif]-->
      <table role="presentation" width="100%" cellpadding="0" cellspacing="0" class="${innerTableClass}" style="${TABLE_RESET} table-layout: fixed;">
        <tr>
          ${columnHtml}
        </tr>
      </table>
      <!--[if mso]></tr></table><![endif]-->
    </td>
  </tr>
</table>`;
}

// ---- Social layer ---------------------------------------------------------

function compileSocialLayer(layer: SocialLayer, _gs: GlobalStyles): string {
  // Only render enabled platforms that have a URL — an icon with no link is
  // dropped from the sent email rather than shown as a dead button.
  const enabledLinks = layer.links.filter((l) => l.enabled && l.url.trim() !== '');
  if (!enabledLinks.length) {
    return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'social')}${visibilityClass(layer)}>
  <tr><td style="padding: ${spacingCss(layer.padding)}; text-align: ${layer.align};">&nbsp;</td></tr>
</table>`;
  }

  const icons = enabledLinks
    .map((link) => {
      const label = link.platform.charAt(0).toUpperCase() + link.platform.slice(1);
      const showText = layer.style !== 'icons-only';
      const showIcon = layer.style !== 'text-only';
      const content = [
        showIcon ? socialIconHtml(link.platform, layer.iconSize, layer.iconColor, layer.logos?.[link.platform]) : '',
        showText ? `<span style="vertical-align: middle; margin-left: 4px;">${label}</span>` : '',
      ]
        .filter(Boolean)
        .join('');
      const href = socialHref(link.platform, link.url);
      const inner = href
        ? `<a data-ftp-social="${escHtml(link.platform)}" href="${escHtml(href)}" target="_blank" title="${escHtml(label)}" style="text-decoration: none; color: inherit;">${content}</a>`
        : content;
      return `<td style="padding: 0 ${layer.spacing}px;">${inner}</td>`;
    })
    .join('\n');

  const tdStyle = [
    `padding: ${spacingCss(layer.padding)};`,
    `text-align: ${layer.align};`,
    layer.iconColor ? `color: ${layer.iconColor};` : '',
    layer.lineHeight ? `line-height: ${layer.lineHeight};` : '',
    layer.lineHeight ? 'mso-line-height-rule: exactly;' : '',
    !layer.bgImage && layer.bgColor ? `background-color: ${layer.bgColor};` : '',
    borderCss(layer.border),
  ]
    .filter(Boolean)
    .join(' ');

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'social')}${visibilityClass(layer)}>
  <tr>
    <td style="${tdStyle}">
      <table role="presentation" cellpadding="0" cellspacing="0" align="${layer.align}" style="${TABLE_RESET}">
        <tr>${icons}</tr>
      </table>
    </td>
  </tr>
</table>`;
}

// ---- Video layer ----------------------------------------------------------

function compileVideoLayer(layer: VideoLayer, _gs: GlobalStyles, maxWidth: number): string {
  const tdStyle = [
    `padding: ${spacingCss(layer.padding)};`,
    'text-align: center;',
    !layer.bgImage && layer.bgColor ? `background-color: ${layer.bgColor};` : '',
    borderCss(layer.border),
  ]
    .filter(Boolean)
    .join(' ');

  const thumbnail = layer.thumbnailSrc || 'https://placehold.co/600x338/333333/ffffff?text=Video';

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'video')}${visibilityClass(layer)}>
  <tr>
    <td style="${tdStyle}">
      <a href="${escHtml(normalizeUrl(layer.videoUrl))}" target="_blank" style="text-decoration: none; display: inline-block; position: relative;">
        <img src="${escHtml(thumbnail)}" alt="${escHtml(layer.alt)}" width="${maxWidth}" border="0" style="display: block; max-width: ${maxWidth}px; width: 100%; height: auto;" />
      </a>
    </td>
  </tr>
</table>`;
}

// ---- Countdown layer ------------------------------------------------------

function compileCountdownLayer(layer: CountdownLayer, _gs: GlobalStyles): string {
  const tdStyle = [
    `padding: ${spacingCss(layer.padding)};`,
    'text-align: center;',
    !layer.bgImage && layer.bgColor ? `background-color: ${layer.bgColor};` : '',
    borderCss(layer.border),
  ]
    .filter(Boolean)
    .join(' ');

  // NOTE: a live animated countdown requires a real countdownmail.com project
  // hash (per-timer) that this editor does not yet generate. Until that
  // integration exists we ship a static placeholder rather than a broken GIF so
  // preview/exports have no dead link. (targetDate/timezone are consumed once
  // that integration lands.)
  const countdownSrc = 'https://placehold.co/500x100/f0f0f0/333333?text=Countdown+timer';

  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${dataAttrs(layer.id, 'countdown')}${visibilityClass(layer)}>
  <tr>
    <td style="${tdStyle}">
      <img src="${countdownSrc}" alt="Countdown" width="500" style="display: block; margin: 0 auto; max-width: 100%; height: auto;" />
    </td>
  </tr>
</table>`;
}

// ---- Main layer dispatcher ------------------------------------------------

function compileLayer(layer: Layer, gs: GlobalStyles, maxWidth: number): string {
  const hasBg = layer.bgColor || layer.bgImage;
  let layerBgOpen = '';
  let layerBgClose = '';

  if (hasBg) {
    // The block cell inside also carries the background; round this wrapper
    // too, or a square backdrop bleeds out behind the rounded corners.
    const radius = layer.border?.radius ? ` border-radius: ${layer.border.radius}px; overflow: hidden;` : '';
    layerBgOpen = `<div style="${bgCss(layer)}${radius}">`;
    layerBgClose = '</div>';
  }

  let inner: string;
  switch (layer.type) {
    case 'text':
      inner = compileTextLayer(layer, gs);
      break;
    case 'image':
      inner = compileImageLayer(layer, gs, maxWidth);
      break;
    case 'button':
      inner = compileButtonLayer(layer, gs);
      break;
    case 'divider':
      inner = compileDividerLayer(layer, gs);
      break;
    case 'spacer':
      inner = compileSpacerLayer(layer);
      break;
    case 'html':
      inner = compileHtmlLayer(layer);
      break;
    case 'columns':
      inner = compileColumnsLayer(layer, gs, maxWidth);
      break;
    case 'social':
      inner = compileSocialLayer(layer, gs);
      break;
    case 'video':
      inner = compileVideoLayer(layer, gs, maxWidth);
      break;
    case 'countdown':
      inner = compileCountdownLayer(layer, gs);
      break;
  }

  const margined = inner !== '' && hasSpacing(layer.margin);

  let wrapped = `${layerBgOpen}${inner}${layerBgClose}`;
  if (layer.bgImage) {
    // The VML background rect must fit inside the margin area in Outlook.
    const vmlWidth = margined ? maxWidth - layer.margin.left - layer.margin.right : maxWidth;
    wrapped = vmlBgImage(layer, wrapped, vmlWidth);
  }

  // Outer spacing: transparent space around the block's border/background.
  // Compiled as padding on a wrapping table cell because Outlook's Word engine
  // ignores CSS margin. Skipped entirely at zero so existing emails compile
  // unchanged, and skipped when the block compiled to nothing (link-less
  // button) so no phantom blank strip ships.
  if (margined) {
    wrapped = `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="${TABLE_RESET}"${visibilityClass(layer)}>
  <tr><td style="padding: ${spacingCss(layer.margin)};">${wrapped}</td></tr>
</table>`;
  }

  const lockedAttr = layer.locked ? ' data-dnd-locked="true"' : '';
  return `<div data-dnd-layer-id="${escHtml(layer.id)}" data-dnd-layer-type="${escHtml(layer.type)}"${lockedAttr}>${wrapped}</div>`;
}

// ---- Responsive style block -----------------------------------------------

function buildResponsiveStyles(maxWidth: number): string {
  return `
    @media screen and (max-width: ${maxWidth + 40}px) {
      .dnd-wrapper { width: 100% !important; max-width: 100% !important; }
      .dnd-column-stack { display: block !important; width: 100% !important; max-width: 100% !important; padding-right: 0 !important; }
      /* Reverse stack order: lay cells out right-to-left as inline-blocks so
         they visually stack in reverse while keeping accessible DOM order. */
      .dnd-columns-reverse tr { direction: rtl !important; }
      .dnd-columns-reverse .dnd-column-stack { direction: ltr !important; display: inline-block !important; width: 100% !important; max-width: 100% !important; vertical-align: top; }
      .dnd-hide-mobile { display: none !important; max-height: 0 !important; overflow: hidden !important; mso-hide: all !important; }
      img { max-width: 100% !important; height: auto !important; }
    }
    @media screen and (min-width: ${maxWidth + 41}px) {
      .dnd-hide-desktop { display: none !important; max-height: 0 !important; overflow: hidden !important; }
    }
  `;
  // NOTE: no forced prefers-color-scheme:dark override. Previously this hard-set
  // .dnd-wrapper to #2d2d2d in every dark-mode client, turning white designs
  // near-black. The user's chosen background now stands; the color-scheme meta
  // tags let clients apply their own (reversible) dark treatment if they wish.
}

// ---- Full document compilation --------------------------------------------

export function compileModel(model: DndEmailModel): string {
  const { globalStyles: gs, wrapper, layers, preheaderText } = model;
  const maxWidth = wrapper.maxWidth;

  // The content column always has a concrete background (never empty → never a
  // transparent/black surprise). The page body stays transparent by default so
  // it inherits the client's inbox background.
  const bodyBg = gs.bodyBgColor;
  const wrapperBg = wrapper.bgColor || gs.contentBgColor || '#ffffff';

  const bodyStyle = [
    'margin: 0;',
    'padding: 0;',
    // Omit when transparent so the email renders on the client's default body.
    bodyBg ? `background-color: ${bodyBg};` : '',
    `-webkit-text-size-adjust: 100%;`,
    `-ms-text-size-adjust: 100%;`,
    fontFamilyCss(gs.fontFamily),
    `font-size: ${gs.fontSize};`,
    `color: ${resolveTextColor(gs.textColor)};`,
    `line-height: ${gs.lineHeight};`,
  ].filter(Boolean).join(' ');

  const wrapperStyle = [
    `max-width: ${maxWidth}px;`,
    'width: 100%;',
    'margin: 0 auto;',
    `padding: ${spacingCss(wrapper.padding)};`,
    `background-color: ${wrapperBg};`,
    TABLE_RESET,
  ].join(' ');

  const layerHtml = layers
    .map((layer) => compileLayer(layer, gs, maxWidth))
    .join('\n');

  const preheader = preheaderText
    ? `<div style="display:none;font-size:1px;color:#ffffff;line-height:1px;max-height:0px;max-width:0px;opacity:0;overflow:hidden;">${escHtml(preheaderText)}</div>`
    : '';

  // Progressive enhancement for clients that keep <style>. The SAME rules are
  // inlined onto each <h1>/<h2>/<h3>/<p>/<a> by inlineContentStyles() at content
  // build time, so rendering does not depend on this block surviving.
  const linkStyles = typographyCss(gs);

  // Only define a web font when something actually asks for it, so an email that
  // uses none costs the recipient no font request. Checked against the compiled
  // output rather than the model's font fields, because a font can also be
  // applied to a selection inside a text block, which lives in the content HTML.
  const webFontHead = webFontHeadHtml(webFontsUsedIn(`${bodyStyle} ${layerHtml}`));

  return `<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" xmlns:v="urn:schemas-microsoft-com:vml" xmlns:o="urn:schemas-microsoft-com:office:office" lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <meta http-equiv="X-UA-Compatible" content="IE=edge" />
  <!-- Light-only: this editor has no dark-mode design, so we opt out of client
       dark treatment rather than let a transparent body paint dark around white
       content. (Revisit if a design-aware dark palette is added.) -->
  <meta name="color-scheme" content="light" />
  <meta name="supported-color-schemes" content="light" />
  <title></title>
  <!--[if mso]>
  <noscript>
    <xml>
      <o:OfficeDocumentSettings>
        <o:AllowPNG/>
        <o:PixelsPerInch>96</o:PixelsPerInch>
      </o:OfficeDocumentSettings>
    </xml>
  </noscript>
  <![endif]-->
${webFontHead}
  <style type="text/css">
    body { margin: 0; padding: 0; }
    table { ${TABLE_RESET} }
    img { -ms-interpolation-mode: bicubic; border: 0; }
    ${linkStyles}
    ${buildResponsiveStyles(maxWidth)}
  </style>
</head>
<body class="dnd-body"${bodyBg ? ` bgcolor="${bodyBg}"` : ''} style="${bodyStyle}" ${DND_HTML_MARKER}>
  ${preheader}
  <!--[if mso]>
  <table role="presentation" width="${maxWidth}" cellpadding="0" cellspacing="0" align="center" bgcolor="${wrapperBg}" style="${TABLE_RESET}"><tr><td>
  <![endif]-->
  <table role="presentation" class="dnd-wrapper" width="100%" cellpadding="0" cellspacing="0" align="center" bgcolor="${wrapperBg}" style="${wrapperStyle}" data-ftp-type="wrapper">
    <tr>
      <td>
        ${layerHtml}
      </td>
    </tr>
  </table>
  <!--[if mso]>
  </td></tr></table>
  <![endif]-->
</body>
</html>`;
}

// ---- Compile a single layer (for incremental updates) ---------------------

export function compileSingleLayer(
  layer: Layer,
  gs: GlobalStyles,
  maxWidth: number,
): string {
  return compileLayer(layer, gs, maxWidth);
}
